import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import config from "../config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const baseUrl = config.baseURL;
const RESEND_DELAY = 30;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [counter, setCounter] = useState(0);
  const [sent, setSent] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const timerRef = useRef(null);

  const startCountdown = () => {
    setCounter(RESEND_DELAY);
    timerRef.current = setInterval(() => {
      setCounter((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendResetEmail = async () => {
    if (!email || !email.includes("@")) {
      setEmailError(true);
      setTimeout(() => setEmailError(false), 2000);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(baseUrl + "/auth/forgot-password", {
        email,
      });
      toast.success(response.data.message || "Reset email sent!");
      setSent(true);
      startCountdown();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Error sending reset email. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendResetEmail();
  };

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  return (
    <div style={styles.page}>
      {/* Background circles */}
      <div
        style={{
          ...styles.bgCircle,
          width: 500,
          height: 500,
          top: -200,
          right: -200,
          border: "1px solid rgba(209,216,224,.07)",
        }}
      />
      <div
        style={{
          ...styles.bgCircle,
          width: 320,
          height: 320,
          top: -120,
          right: -120,
          border: "1px solid rgba(209,216,224,.10)",
        }}
      />
      <div
        style={{
          ...styles.bgCircle,
          width: 550,
          height: 550,
          bottom: -260,
          left: -260,
          border: "1px solid rgba(209,216,224,.05)",
        }}
      />

      {/* Nav */}
      <nav style={styles.nav}>
        <div style={styles.logoBox}>
          <span style={styles.logoLetter}>S</span>
        </div>
        <span style={styles.brand}>Spade ERP</span>
      </nav>

      {/* Eyebrow */}
      <div
        style={{
          textAlign: "center",
          marginBottom: 28,
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={styles.eyebrow}>
          <span style={styles.eyebrowDot} />
          <span style={styles.eyebrowText}>ACCOUNT RECOVERY</span>
        </div>
      </div>

      {/* Card */}
      <div style={styles.card}>
        {/* Icon */}
        <div style={styles.cardIcon}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2E4053"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h2 style={styles.heading}>Forgot your password?</h2>
        <p style={styles.subtitle}>
          Enter the email address linked to your Spade account and we'll send
          you a reset link.
        </p>

        {/* Success banner */}
        {sent && (
          <div style={styles.successBanner}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#16a34a"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0, marginTop: 1 }}
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span style={styles.successText}>
              Reset link sent! Check your inbox and follow the instructions. The
              link expires in 15 minutes.
            </span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={styles.fieldWrap}>
            <label style={styles.label} htmlFor="email">
              Email address
            </label>
            <div style={styles.inputWrap}>
              <svg
                style={styles.inputIcon}
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#A6AAAD"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                disabled={loading}
                style={{
                  ...styles.input,
                  borderColor: emailError ? "#e24b4a" : "#D1D8E0",
                }}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || counter > 0}
            style={{
              ...styles.btnPrimary,
              opacity: loading || counter > 0 ? 0.6 : 1,
              cursor: loading || counter > 0 ? "not-allowed" : "pointer",
            }}
            onMouseEnter={(e) => {
              if (!loading && counter === 0)
                e.currentTarget.style.background = "#1C2A3D";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#2E4053";
            }}
          >
            {loading
              ? "Sending…"
              : counter > 0
              ? `Resend in ${counter}s`
              : sent
              ? "Resend reset link"
              : "Send reset link"}
          </button>
        </form>

        {/* Countdown */}
        {counter > 0 && (
          <div style={styles.countdownWrap}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#A6AAAD"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span style={styles.countdownText}>Resend available in</span>
            <span style={styles.countdownNum}>{counter}</span>
            <span style={styles.countdownText}>seconds</span>
          </div>
        )}

        <div style={styles.divider} />

        {/* Back button */}
        <button
          style={styles.btnBack}
          onClick={() => window.location.replace("/login")}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#F4F6F9";
            e.currentTarget.style.borderColor = "#A6AAAD";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "#D1D8E0";
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2E4053"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to login
        </button>
      </div>

      {/* Bottom chips */}
      <div style={styles.chips}>
        {[
          "Secure reset flow",
          "Link expires in 15 min",
          "ERP Dashboard access",
        ].map((chip) => (
          <span key={chip} style={styles.chip}>
            {chip}
          </span>
        ))}
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(135deg, #2E4053 0%, #1a2b3c 40%, #0d1f30 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "100px 24px 48px",
    position: "relative",
    overflow: "hidden",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  bgCircle: {
    position: "absolute",
    borderRadius: "50%",
    pointerEvents: "none",
  },
  nav: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 64,
    display: "flex",
    alignItems: "center",
    padding: "0 32px",
    gap: 10,
  },
  logoBox: {
    width: 30,
    height: 30,
    background: "#D1D8E0",
    borderRadius: 7,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoLetter: {
    fontSize: 14,
    fontWeight: 900,
    color: "#2E4053",
  },
  brand: {
    fontSize: 16,
    fontWeight: 800,
    color: "#fff",
    letterSpacing: -0.5,
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "rgba(209,216,224,.1)",
    border: "1px solid rgba(209,216,224,.2)",
    borderRadius: 30,
    padding: "5px 14px",
  },
  eyebrowDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#4ade80",
    boxShadow: "0 0 8px #4ade80",
    display: "inline-block",
    flexShrink: 0,
  },
  eyebrowText: {
    fontSize: 11,
    fontWeight: 600,
    color: "#D1D8E0",
    letterSpacing: 1,
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    padding: "40px 36px",
    width: "100%",
    maxWidth: 440,
    position: "relative",
    zIndex: 1,
  },
  cardIcon: {
    width: 52,
    height: 52,
    borderRadius: 14,
    background: "#f0f3f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  heading: {
    fontSize: 22,
    fontWeight: 800,
    color: "#2E4053",
    letterSpacing: -0.5,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: "#7F8C8D",
    lineHeight: 1.6,
    marginBottom: 28,
  },
  successBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: 14,
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 10,
    marginBottom: 20,
  },
  successText: {
    fontSize: 13,
    color: "#15803d",
    lineHeight: 1.55,
  },
  fieldWrap: {
    marginBottom: 20,
  },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 700,
    color: "#2E4053",
    letterSpacing: 1,
    textTransform: "uppercase",
    marginBottom: 8,
  },
  inputWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  inputIcon: {
    position: "absolute",
    left: 14,
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    padding: "12px 14px 12px 42px",
    border: "1.5px solid #D1D8E0",
    borderRadius: 10,
    fontSize: 15,
    color: "#2E4053",
    outline: "none",
    background: "#F4F6F9",
    fontFamily: "inherit",
    transition: "border-color .2s",
  },
  btnPrimary: {
    width: "100%",
    padding: 13,
    background: "#2E4053",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 800,
    fontFamily: "inherit",
    marginBottom: 16,
    transition: "background .2s",
  },
  countdownWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: 12,
    background: "#F4F6F9",
    borderRadius: 10,
    marginBottom: 16,
  },
  countdownText: {
    fontSize: 13,
    color: "#7F8C8D",
  },
  countdownNum: {
    fontSize: 13,
    fontWeight: 700,
    color: "#2E4053",
  },
  divider: {
    height: 1,
    background: "#EEF0F3",
    margin: "20px 0",
  },
  btnBack: {
    width: "100%",
    padding: 11,
    background: "transparent",
    color: "#2E4053",
    border: "1.5px solid #D1D8E0",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    transition: "background .15s, border-color .15s",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 7,
    marginTop: 28,
    justifyContent: "center",
    position: "relative",
    zIndex: 1,
  },
  chip: {
    fontSize: 11,
    fontWeight: 600,
    color: "#D1D8E0",
    background: "rgba(209,216,224,.1)",
    border: "1px solid rgba(209,216,224,.15)",
    borderRadius: 20,
    padding: "4px 12px",
  },
};

export default ForgotPassword;
